import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import { Lock, WorkspacePremium } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import { hasAdvancedToolsAccess } from '../utils/subscription';
import {
  createFunctionClient,
  mountAdvancedAstrologyTools,
  type AdvancedChartOption,
} from '../vendor/advanced-astrology-tools';
import '../vendor/advanced-astrology-tools/styles.css';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

type SavedChart = {
  _id: string;
  birthDetails?: {
    name?: string;
    placeOfBirth?: { latitude?: number; longitude?: number };
  };
};

const AdvancedTools: React.FC = () => {
  const { id: routeChartId } = useParams<{ id?: string }>();
  const { state } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [charts, setCharts] = useState<SavedChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasAccess = hasAdvancedToolsAccess(state.user);
  const chartLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0] === 'hi' ? 'hi' : 'en';

  useEffect(() => {
    if (!state.token || !hasAccess) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    fetch(`${API_BASE_URL}/charts/my-charts?page=1&limit=50&type=individual`, {
      headers: { Authorization: `Bearer ${state.token}` },
      signal: controller.signal,
    })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.message || 'Could not load saved charts.');
        setCharts(body.data?.charts || []);
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [hasAccess, state.token]);

  const selectedChart = useMemo(
    () => charts.find((chart) => chart._id === routeChartId) || charts[0],
    [charts, routeChartId],
  );

  useEffect(() => {
    if (!rootRef.current || !selectedChart || !state.token || !hasAccess) return;
    const chartOptions: AdvancedChartOption[] = charts.map((chart) => ({
      id: chart._id,
      name: chart.birthDetails?.name || 'Saved chart',
    }));
    const place = selectedChart.birthDetails?.placeOfBirth;
    const client = createFunctionClient(async (params, options) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
      });
      const response = await fetch(`${API_BASE_URL}/advanced-astrology/snapshot?${query}`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${state.token}` },
        signal: options?.signal,
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || body.error || 'Snapshot request failed.');
      return body;
    });
    const module = mountAdvancedAstrologyTools(rootRef.current, {
      client,
      title: 'Professional Tools',
      charts: chartOptions,
      chartId: selectedChart._id,
      timezone: state.user?.preferences?.timezone || 'Asia/Kolkata',
      latitude: Number(place?.latitude ?? 22.5726),
      longitude: Number(place?.longitude ?? 88.3639),
      ayanamsha: String(state.user?.preferences?.ayanamshaMode || 'LAHIRI').toLowerCase(),
      nodeMode: state.user?.preferences?.nodeMode || 'true',
      futureYears: 10,
      restrictToFuture: false,
      expertMode: false,
      disclaimerLanguage: chartLanguage,
    });
    return () => module.destroy();
  }, [chartLanguage, charts, hasAccess, selectedChart, state.token, state.user]);

  if (!hasAccess) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Lock color="primary" sx={{ fontSize: 54, mb: 2 }} />
          <Typography variant="h4" gutterBottom>Advanced Tools</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Gochar Transit Wheel, North Indian Gochar Rashi Chart, Sarvatobhadra Chakra, Kota Chakra,
            Gochara Kaksha, Western Aspect Analysis / Aspect Matrix and Planetary Position Tables.
          </Typography>
          <Button variant="contained" startIcon={<WorkspacePremium />} onClick={() => navigate('/pricing')}>
            View Studio Pro
          </Button>
        </Box>
      </Container>
    );
  }

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 420 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!charts.length) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Advanced Tools</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>Create and save a birth chart before opening these tools.</Typography>
        <Button variant="contained" onClick={() => navigate('/calculate')}>Create Birth Chart</Button>
      </Container>
    );
  }

  return <Box sx={{ px: { xs: 1, md: 2 }, py: 2, overflowX: 'auto' }}><div ref={rootRef} /></Box>;
};

export default AdvancedTools;
